import { useEffect, useRef, useState } from "react";

type EventCategory = 'bigtech' | 'economia' | 'tecnologia' | 'sistemas' | 'ciberseguridad';

interface TimelineEvent {
  title: string;
  comment: string;
  category: EventCategory;
  isBigTech?: boolean;
  isGlobaltis?: boolean;
}

interface TimelineItem {
  year: number;
  events: TimelineEvent[];
  isGlobaltis?: boolean;
  hasBigTech?: boolean;
}

const CATEGORY_ORDER: EventCategory[] = ['bigtech', 'economia', 'tecnologia', 'sistemas', 'ciberseguridad'];

const CATEGORY_LABELS: Record<EventCategory, string> = {
  bigtech: 'BIG TECH',
  economia: 'ECONOMÍA',
  tecnologia: 'TECNOLOGÍA',
  sistemas: 'SISTEMAS',
  ciberseguridad: 'CIBERSEGURIDAD'
};

const timelineData: TimelineItem[] = [
  // === ERA PRE-DIGITAL (1975-1989) ===
  { year: 1975, events: [
    { title: "Nace Microsoft", comment: "Bill Gates y Paul Allen fundan la futura gigante del software", category: 'bigtech', isBigTech: true },
    { title: "Fin crisis del petróleo", comment: "Recesión global: inflación 12%, paro al 9% en EEUU", category: 'economia' }
  ], hasBigTech: true },
  { year: 1976, events: [
    { title: "Nace Apple", comment: "Jobs y Wozniak en un garaje de California", category: 'bigtech', isBigTech: true }
  ], hasBigTech: true },
  { year: 1977, events: [
    { title: "Apple II", comment: "El primer ordenador personal de éxito masivo", category: 'tecnologia' },
    { title: "Primeros videoclubs", comment: "Nace el alquiler de películas en VHS", category: 'tecnologia' }
  ]},
  { year: 1980, events: [
    { title: "IPO de Apple", comment: "$1.3B valoración, la mayor desde Ford en 1956", category: 'economia' }
  ]},
  { year: 1981, events: [
    { title: "IBM PC", comment: "El ordenador personal llega a las empresas", category: 'tecnologia' },
    { title: "MS-DOS", comment: "Microsoft licencia el sistema operativo a IBM", category: 'sistemas' }
  ]},
  { year: 1984, events: [
    { title: "Macintosh original", comment: "El anuncio de Super Bowl cambia la publicidad para siempre", category: 'tecnologia' }
  ]},
  { year: 1985, events: [
    { title: "Windows 1.0", comment: "Microsoft entra en el juego de las interfaces gráficas", category: 'sistemas' },
    { title: "Nace Blockbuster", comment: "El gigante del videoclub con 9.000 tiendas", category: 'tecnologia' }
  ]},
  { year: 1986, events: [
    { title: "IPO de Microsoft", comment: "$21K invertidos entonces serían $50M+ hoy", category: 'economia' }
  ]},
  { year: 1987, events: [
    { title: "Lunes Negro", comment: "Dow Jones cae 22% en un día, nace el circuit breaker", category: 'economia' }
  ]},
  { year: 1988, events: [
    { title: "Mac OS 6", comment: "Nuestros primeros pasos con Apple", category: 'sistemas' },
    { title: "Morris Worm", comment: "El primer gusano de internet infecta 6.000 ordenadores", category: 'ciberseguridad' }
  ]},
  
  // === ERA PRE-INTERNET (1990-1994) ===
  { year: 1990, events: [
    { title: "Recesión del 90", comment: "Guerra del Golfo + crisis S&L: EEUU en recesión 8 meses", category: 'economia' },
    { title: "Windows 3.0", comment: "3 millones de copias vendidas en 2 meses", category: 'sistemas' }
  ]},
  { year: 1991, events: [
    { title: "World Wide Web", comment: "Tim Berners-Lee publica la primera página web", category: 'tecnologia' },
    { title: "GSM 2G", comment: "Los móviles empiezan a hablar digital", category: 'tecnologia' },
    { title: "Linux", comment: "Linus Torvalds libera el kernel que revolucionaría los servidores", category: 'sistemas' }
  ]},
  { year: 1993, events: [
    { title: "Mosaic", comment: "El primer navegador gráfico, la web se vuelve visual", category: 'tecnologia' },
    { title: "CD-ROM", comment: "650MB parecían infinitos", category: 'tecnologia' }
  ]},
  { year: 1994, events: [
    { title: "Nace Amazon", comment: "Jeff Bezos empieza vendiendo libros en un garaje", category: 'bigtech', isBigTech: true },
    { title: "Netscape Navigator", comment: "La guerra de los navegadores comienza", category: 'tecnologia' }
  ], hasBigTech: true },
  
  // === ERA INTERNET DIAL-UP (1995-2000) ===
  { year: 1995, events: [
    { title: "IPO de Netscape", comment: "De $28 a $75 en un día, comienza la fiebre .com", category: 'economia' },
    { title: "eBay + Craigslist", comment: "El comercio P2P y los clasificados se digitalizan", category: 'tecnologia' },
    { title: "Windows 95", comment: "Start me up: 7 millones de copias en 5 semanas", category: 'sistemas' }
  ]},
  { year: 1997, events: [
    { title: "Crisis asiática", comment: "El baht tailandés colapsa, efecto dominó en Asia", category: 'economia' },
    { title: "IPO de Amazon", comment: "$18/acción, Bezos prometió pérdidas por años", category: 'economia' },
    { title: "DVD", comment: "El sucesor del VHS conquista los hogares", category: 'tecnologia' },
    { title: "Netflix (DVDs)", comment: "Reed Hastings reinventa el alquiler de películas", category: 'tecnologia' },
    { title: "Deep Blue vence a Kasparov", comment: "Primera vez que una máquina gana al campeón mundial", category: 'tecnologia' }
  ]},
  { year: 1998, events: [
    { title: "Nace Google", comment: "Page y Brin reinventan la búsqueda desde Stanford", category: 'bigtech', isBigTech: true },
    { title: "iMac G3", comment: "Apple renace con colores y sin disquetera", category: 'tecnologia' },
    { title: "PayPal", comment: "Pagos online sin compartir tu tarjeta", category: 'tecnologia' }
  ], hasBigTech: true },
  { year: 1999, events: [
    { title: "WiFi 802.11b", comment: "Internet sin cables, 11 Mbps de libertad", category: 'tecnologia' },
    { title: "Napster", comment: "18 meses después, la industria musical nunca sería igual", category: 'tecnologia' },
    { title: "Salesforce", comment: "Nace el SaaS y el 'No Software'", category: 'tecnologia' }
  ]},
  { year: 2000, events: [
    { title: "Burbuja .com explota", comment: "Nasdaq cae 78%, $5 trillones evaporados en 2 años", category: 'economia' },
    { title: "USB Flash Drives", comment: "Adiós disquetes, hola memorias de bolsillo", category: 'tecnologia' },
    { title: "ILOVEYOU virus", comment: "El malware más destructivo: $10B en daños globales", category: 'ciberseguridad' }
  ]},
  
  // === ERA BANDA ANCHA (2001-2006) ===
  { year: 2001, events: [
    { title: "Crisis post-11S", comment: "Los mercados cierran 4 días, mayor caída desde 1929", category: 'economia' },
    { title: "iPod", comment: "1.000 canciones en tu bolsillo cambia la música", category: 'tecnologia' },
    { title: "Wikipedia", comment: "El conocimiento libre y colaborativo", category: 'tecnologia' },
    { title: "Mac OS X", comment: "Apple reinventa su sistema: Unix + interfaz Aqua", category: 'sistemas' },
    { title: "Windows XP", comment: "El sistema operativo más longevo de Microsoft", category: 'sistemas' }
  ]},
  { year: 2002, events: [
    { title: "Escándalo Enron/WorldCom", comment: "Las mayores quiebras corporativas, nace Sarbanes-Oxley", category: 'economia' }
  ]},
  { year: 2003, events: [
    { title: "Nace Globaltis", comment: "Empezamos esta aventura tecnológica", category: 'bigtech', isGlobaltis: true },
    { title: "Skype", comment: "Videollamadas gratis que desafían a las telecos", category: 'tecnologia' },
    { title: "LinkedIn", comment: "El networking profesional se digitaliza", category: 'tecnologia' }
  ], isGlobaltis: true },
  { year: 2004, events: [
    { title: "Nace Facebook", comment: "Zuckerberg conecta Harvard, luego el mundo", category: 'bigtech', isBigTech: true },
    { title: "IPO de Google", comment: "$23B valoración inicial, hoy vale $2 trillones", category: 'economia' },
    { title: "Gmail", comment: "1GB gratis cuando Hotmail ofrecía 2MB", category: 'tecnologia' }
  ], hasBigTech: true },
  { year: 2005, events: [
    { title: "YouTube", comment: "'Me at the zoo', el primer vídeo que cambió todo", category: 'tecnologia' },
    { title: "Google compra Android", comment: "$50M por el futuro sistema operativo móvil", category: 'tecnologia' }
  ]},
  { year: 2006, events: [
    { title: "Google compra YouTube", comment: "$1.65B por 18 meses de historia", category: 'economia' },
    { title: "Twitter", comment: "140 caracteres para cambiar las noticias", category: 'tecnologia' },
    { title: "AWS lanza EC2", comment: "Nace la nube pública, todo cambia para las startups", category: 'tecnologia' },
    { title: "Blu-ray", comment: "La alta definición gana la guerra de formatos", category: 'tecnologia' }
  ]},
  
  // === ERA SMARTPHONE (2007-2012) ===
  { year: 2007, events: [
    { title: "Crisis subprime comienza", comment: "Bear Stearns cierra dos hedge funds", category: 'economia' },
    { title: "iPhone", comment: "¿Un iPod, teléfono y navegador? Revolucionario", category: 'tecnologia' },
    { title: "Kindle", comment: "Amazon reinventa el libro, otra vez", category: 'tecnologia' },
    { title: "Mac OS X Leopard", comment: "Time Machine y Spaces revolucionan el backup", category: 'sistemas' },
    { title: "Windows Vista", comment: "El fracaso que preparó el éxito de Windows 7", category: 'sistemas' },
    { title: "Ciberataque a Estonia", comment: "Primer ataque de estado-nación, paraliza un país", category: 'ciberseguridad' }
  ]},
  { year: 2008, events: [
    { title: "Colapso de Lehman Brothers", comment: "El mayor crash desde 1929, $700B de rescate", category: 'economia' },
    { title: "App Store", comment: "500 apps el día 1, hoy más de 2 millones", category: 'tecnologia' },
    { title: "Spotify", comment: "La música por streaming legal llega a Europa", category: 'tecnologia' },
    { title: "Android + Chrome", comment: "Google contraataca en móvil y navegador", category: 'sistemas' }
  ]},
  { year: 2009, events: [
    { title: "S&P 500 toca fondo", comment: "666 puntos en marzo, comienza el bull market más largo", category: 'economia' },
    { title: "Bitcoin", comment: "Satoshi mina el bloque génesis, nace el blockchain", category: 'economia' },
    { title: "WhatsApp", comment: "El SMS tiene los días contados", category: 'tecnologia' },
    { title: "Uber", comment: "Travis Kalanick reinventa el taxi", category: 'tecnologia' },
    { title: "Windows 7", comment: "El sistema que restauró la confianza en Microsoft", category: 'sistemas' },
    { title: "Mac OS X Snow Leopard", comment: "Cero funciones nuevas, 100% optimización", category: 'sistemas' }
  ]},
  { year: 2010, events: [
    { title: "Crisis deuda europea", comment: "Grecia rescatada, España e Italia tiemblan", category: 'economia' },
    { title: "Flash Crash", comment: "Dow Jones cae 1.000 puntos en minutos por trading algorítmico", category: 'economia' },
    { title: "iPad", comment: "La tablet que todos querían sin saberlo", category: 'tecnologia' },
    { title: "Instagram", comment: "Fotos con filtros, 25.000 usuarios el día 1", category: 'tecnologia' },
    { title: "Stuxnet descubierto", comment: "Primera ciberarma: sabotea el programa nuclear iraní", category: 'ciberseguridad' }
  ]},
  { year: 2011, events: [
    { title: "Crisis deuda España", comment: "Prima de riesgo a 500pb, rescate bancario inminente", category: 'economia' },
    { title: "Siri", comment: "El asistente de voz llega al bolsillo de millones", category: 'tecnologia' },
    { title: "iCloud", comment: "Tu vida sincronizada en todos tus dispositivos", category: 'tecnologia' },
    { title: "Snapchat", comment: "Los mensajes que desaparecen conquistan a los jóvenes", category: 'tecnologia' }
  ]},
  { year: 2012, events: [
    { title: "IPO de Facebook", comment: "$104B, la mayor salida a bolsa tech hasta entonces", category: 'economia' },
    { title: "Facebook compra Instagram", comment: "$1B por 13 empleados y 0 ingresos", category: 'economia' },
    { title: "Tesla Model S", comment: "El coche eléctrico se vuelve deseable", category: 'tecnologia' },
    { title: "Windows 8", comment: "La interfaz Metro divide a usuarios y críticos", category: 'sistemas' },
    { title: "OS X Mountain Lion", comment: "iMessage y Notificaciones llegan al Mac", category: 'sistemas' }
  ]},
  
  // === ERA CLOUD Y WEARABLES (2013-2018) ===
  { year: 2013, events: [
    { title: "Netflix streaming España", comment: "El maratón de series se normaliza", category: 'tecnologia' },
    { title: "Snowden y la NSA", comment: "La privacidad digital entra en el debate global", category: 'ciberseguridad' }
  ]},
  { year: 2014, events: [
    { title: "Facebook compra WhatsApp", comment: "$19B, la mayor adquisición tech", category: 'economia' },
    { title: "Alibaba IPO", comment: "$25B, récord absoluto en bolsa", category: 'economia' },
    { title: "Apple Watch anunciado", comment: "El wearable que revive la industria relojera", category: 'tecnologia' },
    { title: "Amazon Echo + Alexa", comment: "El altavoz inteligente entra en el hogar", category: 'tecnologia' },
    { title: "Heartbleed", comment: "Bug en OpenSSL expone el 17% de servidores web seguros", category: 'ciberseguridad' }
  ]},
  { year: 2015, events: [
    { title: "Crash bursátil en China", comment: "Shanghai cae 40%, $3 trillones evaporados en semanas", category: 'economia' },
    { title: "Apple Music", comment: "Cupertino entra en streaming, Spotify tiembla", category: 'tecnologia' },
    { title: "Tesla Autopilot", comment: "La conducción semi-autónoma llega al consumidor", category: 'tecnologia' },
    { title: "USB-C", comment: "Un cable para gobernarlos a todos", category: 'tecnologia' },
    { title: "Windows 10", comment: "'El último Windows': actualizaciones continuas", category: 'sistemas' },
    { title: "OS X El Capitan", comment: "Split View y Metal mejoran productividad", category: 'sistemas' }
  ]},
  { year: 2016, events: [
    { title: "Brexit shock", comment: "La libra cae 10%, mercados en pánico", category: 'economia' },
    { title: "TikTok (Douyin)", comment: "Vídeos cortos que capturan a la Gen Z", category: 'tecnologia' },
    { title: "Google Home", comment: "La guerra de los asistentes se intensifica", category: 'tecnologia' }
  ]},
  { year: 2017, events: [
    { title: "Bitcoin a $20K", comment: "La primera burbuja cripto, 1900% en un año", category: 'economia' },
    { title: "Face ID + AirPods", comment: "Tu cara es tu contraseña + audio sin cables", category: 'tecnologia' },
    { title: "WannaCry", comment: "Ransomware paraliza hospitales y empresas en 150 países", category: 'ciberseguridad' },
    { title: "NotPetya", comment: "El ciberataque más costoso: $10B en daños", category: 'ciberseguridad' }
  ]},
  { year: 2018, events: [
    { title: "Apple vale $1 Trillón", comment: "Primera empresa en la historia en alcanzarlo", category: 'economia' },
    { title: "RGPD", comment: "Europa lidera la regulación de privacidad global", category: 'ciberseguridad' },
    { title: "Cambridge Analytica", comment: "El escándalo que cambió Facebook para siempre", category: 'ciberseguridad' }
  ]},
  
  // === ERA 5G E IA (2019-2025) ===
  { year: 2019, events: [
    { title: "5G primeras redes", comment: "Hasta 10 Gbps, latencia de 1ms", category: 'tecnologia' },
    { title: "Supremacía Cuántica", comment: "Google resuelve en 200s lo que tardaría 10.000 años", category: 'tecnologia' },
    { title: "Disney+", comment: "La guerra del streaming se intensifica", category: 'tecnologia' }
  ]},
  { year: 2020, events: [
    { title: "Tesla entra en S&P 500", comment: "$700B de market cap añadidos al índice", category: 'economia' },
    { title: "GameStop comienza", comment: "r/WallStreetBets desafía a los hedge funds", category: 'economia' },
    { title: "Pandemia + Zoom", comment: "De 10M a 300M de usuarios diarios en 3 meses", category: 'tecnologia' },
    { title: "Apple M1", comment: "ARM en Mac, el fin de Intel en Cupertino", category: 'tecnologia' },
    { title: "macOS Big Sur", comment: "Rediseño total para la era Apple Silicon", category: 'sistemas' },
    { title: "SolarWinds hack", comment: "Rusia infiltra 18.000 organizaciones vía actualización", category: 'ciberseguridad' }
  ]},
  { year: 2021, events: [
    { title: "GameStop saga", comment: "Melvin Capital pierde 53%, trading democratizado", category: 'economia' },
    { title: "NFTs explotan", comment: "Beeple vende por $69M, récord en arte digital", category: 'economia' },
    { title: "Bitcoin a $69K", comment: "ATH histórico, El Salvador lo adopta como moneda", category: 'economia' },
    { title: "Facebook → Meta", comment: "Zuckerberg apuesta todo al metaverso", category: 'bigtech' },
    { title: "Windows 11", comment: "Nuevo diseño, widgets y soporte para apps Android", category: 'sistemas' },
    { title: "macOS Monterey", comment: "Universal Control conecta Mac e iPad", category: 'sistemas' },
    { title: "Log4Shell", comment: "Vulnerabilidad crítica afecta a millones de servidores", category: 'ciberseguridad' }
  ]},
  { year: 2022, events: [
    { title: "Cripto-invierno", comment: "FTX colapsa, Bitcoin cae a $16K desde $69K", category: 'economia' },
    { title: "Inflación global", comment: "Fed sube tipos al ritmo más rápido en 40 años", category: 'economia' },
    { title: "ChatGPT", comment: "1M de usuarios en 5 días, récord absoluto", category: 'tecnologia' },
    { title: "Midjourney + DALL-E 2", comment: "La IA generativa de imágenes explota", category: 'tecnologia' }
  ]},
  { year: 2023, events: [
    { title: "Nvidia supera $1 Trillón", comment: "La IA dispara las acciones un 200% en un año", category: 'economia' },
    { title: "Crisis bancaria US", comment: "SVB, Signature y First Republic caen en semanas", category: 'economia' },
    { title: "GPT-4", comment: "IA multimodal, supera exámenes de medicina y derecho", category: 'tecnologia' },
    { title: "GitHub Copilot masivo", comment: "El 46% del código nuevo es generado por IA", category: 'tecnologia' }
  ]},
  { year: 2024, events: [
    { title: "Nvidia supera $3 Trillones", comment: "Brevemente la empresa más valiosa del mundo", category: 'economia' },
    { title: "Apple Vision Pro", comment: "Computación espacial, $3.500 por el futuro", category: 'tecnologia' },
    { title: "Sora + Gemini Ultra", comment: "Vídeo generativo y multimodalidad avanzada", category: 'tecnologia' },
    { title: "Tesla Robotaxi anunciado", comment: "Musk promete taxis autónomos sin volante", category: 'tecnologia' },
    { title: "macOS Sequoia", comment: "Apple Intelligence llega al Mac", category: 'sistemas' },
    { title: "Windows Copilot+", comment: "IA integrada en el sistema operativo", category: 'sistemas' }
  ]},
  { year: 2025, events: [
    { title: "Bitcoin rompe $100K", comment: "La barrera psicológica cae tras el halving", category: 'economia' },
    { title: "GPT-5 + Claude 4", comment: "Razonamiento avanzado, agentes autónomos reales", category: 'tecnologia' },
    { title: "Gemini 2.0 + Project Astra", comment: "Google apuesta por agentes IA universales", category: 'tecnologia' },
    { title: "Nvidia Blackwell", comment: "La GPU más potente para IA: 20 petaflops", category: 'tecnologia' },
    { title: "Humanoides en fábricas", comment: "Tesla Optimus y Figure trabajan en producción", category: 'tecnologia' },
    { title: "DeepSeek R1", comment: "China irrumpe en IA abierta, costes 90% menores", category: 'tecnologia' }
  ]},
];

// Group events by category, ordered by CATEGORY_ORDER
const groupEventsByCategory = (events: TimelineEvent[]): Map<EventCategory, TimelineEvent[]> => {
  const grouped = new Map<EventCategory, TimelineEvent[]>();
  
  for (const category of CATEGORY_ORDER) {
    const categoryEvents = events.filter(e => e.category === category);
    if (categoryEvents.length > 0) {
      grouped.set(category, categoryEvents);
    }
  }
  
  return grouped;
};

// Find the index where Globaltis starts (2003)
const globaltisYearIndex = timelineData.findIndex(item => item.isGlobaltis);

interface TimelineSectionProps {
  isDark?: boolean;
}

const TimelineSection = ({ isDark = false }: TimelineSectionProps) => {
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.getAttribute("data-index") || "0");
          if (entry.isIntersecting) {
            setVisibleItems((prev) => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -50px 0px" }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative max-w-4xl mx-auto">
      {/* Timeline line - Two segments: gray before Globaltis, blue after */}
      <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px">
        {/* Gray segment (before 2003) */}
        <div 
          className={`absolute top-0 w-full ${isDark ? 'bg-white/20' : 'bg-black/10'}`}
          style={{ height: `${(globaltisYearIndex / timelineData.length) * 100}%` }}
        />
        {/* Blue segment (from 2003 onwards) */}
        <div 
          className="absolute w-full bg-primary/60"
          style={{ 
            top: `${(globaltisYearIndex / timelineData.length) * 100}%`,
            height: `${((timelineData.length - globaltisYearIndex) / timelineData.length) * 100}%`
          }}
        />
      </div>

      {/* Timeline items */}
      <div className="space-y-0">
        {timelineData.map((item, index) => {
          const isLeft = index % 2 === 0;
          const isVisible = visibleItems.has(index);
          const isAfterGlobaltis = index >= globaltisYearIndex;
          const groupedEvents = groupEventsByCategory(item.events);
          const categoryEntries = Array.from(groupedEvents.entries());

          return (
            <div
              key={item.year}
              ref={(el) => (itemRefs.current[index] = el)}
              data-index={index}
              className={`
                relative flex items-start py-8 md:py-12
                ${isLeft ? "md:flex-row" : "md:flex-row-reverse"}
                transition-all duration-700 ease-out
                ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
              `}
            >
              {/* Content */}
              <div className={`
                w-full md:w-[45%] pl-20 md:pl-0
                ${isLeft ? "md:pr-16 md:text-right" : "md:pl-16 md:text-left"}
              `}>
                {/* Year - Big number */}
                <div className={`
                  text-4xl md:text-5xl lg:text-6xl font-bold mb-6
                  ${item.hasBigTech
                    ? 'text-amber-500'
                    : item.isGlobaltis 
                      ? 'text-primary' 
                      : isAfterGlobaltis
                        ? 'text-primary/50'
                        : isDark ? 'text-white/40' : 'text-black/20'
                  }
                `}>
                  {item.year}
                </div>
                
                {/* Events grouped by category */}
                <div className="space-y-6">
                  {categoryEntries.map(([category, events], catIndex) => (
                    <div key={category}>
                      {/* Category label - Apple minimalist style */}
                      <div className={`
                        text-[10px] tracking-[0.2em] font-medium mb-3
                        ${isDark ? 'text-white/40' : 'text-black/30'}
                      `}>
                        {CATEGORY_LABELS[category]}
                      </div>
                      
                      {/* Events in this category */}
                      <div className="space-y-2">
                        {events.map((event, eventIndex) => (
                          <div key={eventIndex}>
                            {/* Title */}
                            <h3 className={`
                              text-xl md:text-2xl font-light mb-0.5
                              ${event.isBigTech
                                ? 'text-amber-500 font-medium'
                                : event.isGlobaltis 
                                  ? 'text-primary font-medium' 
                                  : isDark ? 'text-white' : 'text-black'
                              }
                            `}>
                              {event.title}
                            </h3>
                            
                            {/* Comment */}
                            <p className={`
                              text-base md:text-lg font-light
                              ${isDark ? 'text-white/60' : 'text-black/50'}
                            `}>
                              {event.comment}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Center dot */}
              <div className={`
                absolute left-8 md:left-1/2 top-10 md:top-14
                w-4 h-4 rounded-full -translate-x-1/2
                ${item.hasBigTech
                  ? 'bg-amber-500 w-5 h-5 shadow-[0_0_15px_rgba(245,158,11,0.5)]'
                  : item.isGlobaltis 
                    ? 'bg-primary w-6 h-6 shadow-[0_0_20px_hsl(var(--primary)/0.5)]' 
                    : isAfterGlobaltis
                      ? 'bg-primary/60'
                      : isDark ? 'bg-white/40' : 'bg-black/20'
                }
                transition-all duration-300
              `} />

              {/* Empty space for the other side */}
              <div className="hidden md:block w-[45%]" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TimelineSection;
